import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from './ToastContext';
import { useI18n } from '../i18n';
import '../steps/Steps.css';
import { PencilIcon, Trash2Icon } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function RulesSettings({ onClose }) {
    const { showToast } = useToast();
    const { t, lang } = useI18n();
    const [rules, setRules] = useState([]);
    const [newRule, setNewRule] = useState('');
    const [editingRule, setEditingRule] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRules = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/rules`);
            setRules(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching rules:', err);
            const msg = lang === 'ar' ? 'فشل في تحميل قائمة الاشتراطات' : 'Failed to load requirements list';
            setError(msg);
            showToast(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAddRule = async () => {
        if (!newRule.trim()) return;
        try {
            const response = await axios.post(`${API_URL}/rules`, { rule: newRule.trim() });
            setRules(response.data);
            setNewRule('');
            showToast(lang === 'ar' ? 'تمت إضافة الشرط بنجاح' : 'Requirement added successfully', 'success');
        } catch (err) {
            showToast(err.response?.data?.error || (lang === 'ar' ? 'فشل في إضافة الشرط' : 'Failed to add requirement'), 'error');
        }
    };

    const handleDeleteRule = async (rule) => {
        const confirmMsg = lang === 'ar' ? `هل أنت متأكد من حذف الشرط: "${rule}"؟` : `Are you sure you want to delete: "${rule}"?`;
        if (!window.confirm(confirmMsg)) return;
        try {
            const response = await axios.delete(`${API_URL}/rules`, { data: { rule } });
            setRules(response.data);
            showToast(lang === 'ar' ? 'تم حذف الشرط بنجاح' : 'Requirement deleted successfully', 'success');
        } catch (err) {
            showToast(lang === 'ar' ? 'فشل في حذف الشرط' : 'Failed to delete requirement', 'error');
        }
    };

    const handleStartEdit = (rule) => {
        setEditingRule(rule);
        setEditValue(rule);
    };

    const handleUpdateRule = async () => {
        if (!editValue.trim() || editValue === editingRule) {
            setEditingRule(null);
            return;
        }
        try {
            const response = await axios.put(`${API_URL}/rules`, {
                oldRule: editingRule,
                newRule: editValue.trim()
            });
            setRules(response.data);
            setEditingRule(null);
            showToast(lang === 'ar' ? 'تم تحديث الشرط بنجاح' : 'Requirement updated successfully', 'success');
        } catch (err) {
            showToast(err.response?.data?.error || (lang === 'ar' ? 'فشل في تحديث الشرط' : 'Failed to update requirement'), 'error');
        }
    };

    return (
        <div className="settings-overlay">
            <div className="settings-modal">
                <div className="settings-header">
                    <h2>{t('settings.title')}</h2>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="settings-content">
                    <div className="add-rule-form">
                        <input
                            type="text"
                            value={newRule}
                            onChange={(e) => setNewRule(e.target.value)}
                            placeholder={lang === 'ar' ? 'أضف شرطاً جديداً...' : 'Add new requirement...'}
                            className="form-input"
                        />
                        <button onClick={handleAddRule} className="btn-primary">{t('step1.addCustom')}</button>
                    </div>

                    {loading ? (
                        <div className="text-center p-4">{t('common.loading')}</div>
                    ) : error ? (
                        <div className="error-message">{error}</div>
                    ) : (
                        <div className="rules-list-admin">
                            {rules.map((rule, index) => (
                                <div key={index} className="rule-item-admin">
                                    {editingRule === rule ? (
                                        <div className="edit-mode">
                                            <input
                                                type="text"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                className="form-input"
                                                autoFocus
                                            />
                                            <div className="edit-actions">
                                                <button onClick={handleUpdateRule} className="btn-save">{t('common.save')}</button>
                                                <button onClick={() => setEditingRule(null)} className="btn-cancel">{t('common.cancel')}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="rule-text">{rule}</span>
                                            <div className="rule-actions">
                                                <button onClick={() => handleStartEdit(rule)} className="btn-edit"><PencilIcon size={14} color="black" /></button>
                                                <button onClick={() => handleDeleteRule(rule)} className="btn-delete"><Trash2Icon size={14} color="black" /></button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RulesSettings;
